/**
 * The save step's "Text encoding" control (SEO_BRIEF_SMI.md §2.5, contracts
 * 1.7.0 and 1.8.0): UTF-8 or EUC-KR, offered while the file being written is
 * SMI, or SRT when the subtitles are Korean.
 *
 * The words, and the lists of formats it is offered for, are the template's
 * (`ui.save_encoding` in macros/ui.html). This side decides three things only:
 * whether the subtitles are Korean, whether the field shows for the format
 * being written, and which encoding a write in that format uses. All three are
 * pure and exported so they are checked without a DOM.
 *
 * ONE ELEMENT, THREE CALLS. A page mounts it once, calls `sync` wherever its
 * write format changes and `setText` wherever the subtitles change; nothing
 * else about the page has to know the control exists. That keeps it out of the
 * way of the format picker, which is a separate element with its own job.
 */

import type { WriteOptions } from '../../../../../contracts/cue-model.ts';
import { languageFromScript } from '../../../../../src/formats/common/script-language.ts';
import { on, qs } from './dom.ts';

export type SaveEncoding = WriteOptions['encoding'];

/**
 * Cues read to decide whether a file is Korean. `languageFromScript` stops at
 * 20,000 characters, which 500 cues of dialogue reach, and one window of 500
 * rows is a few milliseconds to clone where the whole document is seconds.
 */
export const TEXT_SAMPLE_CUES = 500;

/** A space-separated list of formats on the field's own markup. */
function formatsIn(field: Element | null, attribute: string): readonly string[] {
  return (field?.getAttribute(attribute) ?? '').split(/\s+/).filter((f) => f !== '');
}

/** Every format the field can be offered for, read from its own markup. */
export function offeredFormats(field: Element | null): readonly string[] {
  return formatsIn(field, 'data-save-encoding-formats');
}

/** The ones among them offered only while the subtitles are Korean (SRT). */
export function koreanOnlyFormats(field: Element | null): readonly string[] {
  return formatsIn(field, 'data-save-encoding-korean-only');
}

/**
 * True when the subtitles are Korean: `languageFromScript` names them ko-KR,
 * which it does when Hangul is at least a fifth of the letters. An English
 * file that quotes one Korean word is not, and neither is no text at all.
 */
export function isKoreanText(texts: Iterable<string>): boolean {
  return languageFromScript(texts) === 'ko-KR';
}

/**
 * The formats the field is offered for right now: every one of `offered`
 * when the subtitles are Korean, and without the Korean-only ones when they
 * are not, or when nothing says what they are yet.
 *
 * EUC-KR SRT is for older Korean TVs and set-top boxes. On anything else the
 * choice would only be a way to lose emoji, so the field stays hidden and the
 * write stays UTF-8. SMI is offered whatever the text, as it was before.
 */
export function offeredFor(
  offered: readonly string[],
  koreanOnly: readonly string[],
  korean: boolean,
): readonly string[] {
  return korean ? offered : offered.filter((f) => !koreanOnly.includes(f));
}

/**
 * The encoding a write in `format` uses: the one picked, where the field
 * applies, and UTF-8 everywhere else. `offered` is what `offeredFor` gives.
 *
 * A pick survives a format change on purpose — leave SMI and come back, and
 * EUC-KR is still chosen — but it never leaks into a format it was not offered
 * for. The library refuses EUC-KR for anything but SMI and SRT, so leaking it
 * would turn a format change into a failed download, and an SRT that is not
 * Korean is not offered it, so a pick made on a Korean file stays behind when
 * an English one is dropped.
 *
 * Anything but the exact value 'euc-kr' is UTF-8, the default. Nothing here
 * switches it on its own, Korean text or not.
 */
export function encodingFor(
  format: string | undefined,
  offered: readonly string[],
  picked: string | undefined,
): SaveEncoding {
  if (format === undefined || !offered.includes(format)) return 'utf-8';
  return picked === 'euc-kr' ? 'euc-kr' : 'utf-8';
}

/**
 * The format as the download warning names it. Saved as EUC-KR, an emoji is lost to the
 * encoding, not to the format, and "SMI can't carry everything in this file" blamed the format.
 * "SMI (EUC-KR)" and "SRT (EUC-KR)" make that banner's title and its "nowhere to go in" line
 * both true.
 */
export function labelWithEncoding(label: string, encoding: SaveEncoding): string {
  return encoding === 'euc-kr' ? `${label} (EUC-KR)` : label;
}

/**
 * Written output as text, for a preview. A string is UTF-8 already; bytes are
 * decoded in the encoding they were written in, or EUC-KR Korean previews as
 * mojibake on the one page meant to show it is fine.
 */
export function decodeWritten(output: string | Uint8Array, encoding: SaveEncoding): string {
  return typeof output === 'string' ? output : new TextDecoder(encoding).decode(output);
}

export interface SaveEncodingControl {
  /** Show the field while `format` takes an encoding, hide it otherwise. */
  sync(format: string | undefined): void;
  /**
   * The text of the subtitles being saved, or null when there are none. A
   * Korean-only format (SRT) shows the field only after Korean text arrives.
   */
  setText(texts: Iterable<string> | null): void;
  /** True when whether `format` is offered depends on the text, so a page should send it. */
  needsText(format: string | undefined): boolean;
  /** The encoding a write in `format` uses right now. */
  encodingFor(format: string | undefined): SaveEncoding;
}

/**
 * Mount the field under `root`. `onChange` runs when the user picks another
 * encoding, and should write the file again so the loss count is current
 * before anyone downloads.
 *
 * A page without the field gets a control that always answers UTF-8, so the
 * call sites need no branch.
 */
export function mountSaveEncoding(root: ParentNode, onChange: () => void): SaveEncodingControl {
  const field = qs<HTMLElement>('[data-save-encoding-field]', root);
  const select = field ? qs<HTMLSelectElement>('[data-save-encoding]', field) : null;
  const offered = offeredFormats(field);
  const koreanOnly = koreanOnlyFormats(field).filter((f) => offered.includes(f));
  let korean = false;
  let format: string | undefined;
  const now = () => offeredFor(offered, koreanOnly, korean);
  const show = () => {
    if (field) field.hidden = format === undefined || !now().includes(format);
  };
  if (select) on(select, 'change', onChange);
  return {
    sync(next) {
      format = next;
      show();
    },
    setText(texts) {
      korean = texts !== null && isKoreanText(texts);
      show();
    },
    needsText: (f) => f !== undefined && koreanOnly.includes(f),
    encodingFor: (f) => encodingFor(f, now(), select?.value),
  };
}
